import { type FC, useCallback, useEffect, useRef, useState } from 'react'

import type { OnlyFansMessageMedia } from '@api'
import * as Slider from '@radix-ui/react-slider'
import { useUnit } from 'effector-react'
import { AudioVisualizer } from 'react-audio-visualize'

import { getStreamingUrlFx } from '@/models/vault/medias/streams.ts'
import { AudioPlayPauseButton } from '@/modules/chatting/ui/components/AudioPlayPauseButton'
import { useInViewPort } from '@/modules/chatting/ui/hooks/useInViewport'
import { secondsToTime } from '@/modules/chatting/ui/utils'
import { classNames } from '@/utils/helpers'

import { useAudio } from './useAudio'

interface Props {
  className?: string
  duration: number
  media: OnlyFansMessageMedia
  streamingParams: Partial<{
    creatorId: number
    mediaId: number
    relatedMessageId?: number
    relatedUserId?: number
  }>
  isMediaReady: boolean
}

export const AudioPlayer: FC<Props> = ({
  duration,
  className,
  media,
  streamingParams,
  isMediaReady,
}) => {
  const playerWrapperRef = useRef<HTMLDivElement | null>(null)
  const loaderRef = useRef<HTMLDivElement | null>(null)
  const [isLoadingMediaSource, setIsLoadingMediaSource] = useState(false)
  const [loaderWidth, setLoaderWidth] = useState(0)
  const inView = useInViewPort(playerWrapperRef)

  const {
    initSrc,
    play,
    pause,
    isPlaying,
    isLoading: isLoadingBlob,
    isLoaded: isLoadedBlob,
    currentProgress,
    updateProgress,
    blob,
  } = useAudio()

  useEffect(() => {
    if (!inView) {
      pause()
    }
  }, [inView, pause])

  const handleLoadSteamingSource = useCallback(async () => {
    try {
      setIsLoadingMediaSource(true)
      const res = await getStreamingUrlFx({
        mediaId: media.id!,
        userId: streamingParams.relatedUserId,
        isExpirable: true,
        messageId: streamingParams.relatedMessageId!,
      })

      if (res) {
        initSrc(res)
      }
    } finally {
      setIsLoadingMediaSource(false)
    }
  }, [initSrc, media.id, streamingParams])

  const handlePlayPause = useCallback(() => {
    if (!isLoadedBlob) {
      handleLoadSteamingSource()
    } else if (isPlaying) {
      pause()
    } else {
      play()
    }
  }, [handleLoadSteamingSource, isLoadedBlob, isPlaying, pause, play])

  const handleSliderChange = useCallback(
    (value: number[]) => {
      updateProgress(value[0])
    },
    [updateProgress],
  )

  const progress = [currentProgress]
  const leftTime = duration - currentProgress || 0

  useEffect(() => {
    if (loaderRef.current) {
      setLoaderWidth(loaderRef.current.offsetWidth)
    }
  }, [])

  const isMediaProcessing = !isMediaReady

  return (
    <div
      className={classNames(
        'relative flex w-full min-w-[200px] flex-1 items-center justify-between rounded-lg bg-primary-50 px-1 py-2.5 dark:bg-primary-900',
        className,
      )}
      ref={playerWrapperRef}
    >
      {isMediaProcessing && (
        <div className="absolute left-0 top-0 z-30 h-full w-full bg-primary-50/90  dark:bg-primary-900/90">
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-xs text-title">
              This media is currently being processed
            </p>
          </div>
        </div>
      )}
      {!isLoadingMediaSource && !isLoadingBlob && (
        <div className="h-8 w-8 flex-shrink-0 rounded-full bg-title">
          <AudioPlayPauseButton
            isPlaying={isPlaying}
            onPlay={handlePlayPause}
            onPause={handlePlayPause}
            className="h-full w-full flex-shrink-0"
            iconClassName="w-full flex-shring-0 h-full fill-white dark:fill-secondary-700"
          />
        </div>
      )}
      {blob ? (
        <div className="relative mx-2 flex-1">
          <Slider.Root
            className={classNames(
              'absolute left-0 top-0 flex h-full w-full touch-none select-none items-center',
            )}
            step={1}
            value={progress}
            max={duration}
            min={0}
            onValueChange={handleSliderChange}
          >
            <Slider.Track className="relative h-full w-full grow overflow-hidden opacity-0">
              <Slider.Range className="absolute h-full" />
            </Slider.Track>
            <Slider.Thumb className="block h-full w-1" />
          </Slider.Root>

          <AudioVisualizer
            barPlayedColor="#1E84E3"
            currentTime={currentProgress}
            blob={blob}
            height={37}
            width={loaderWidth}
            barWidth={loaderWidth / 100}
            barColor="#898C9A"
            gap={4}
          />
        </div>
      ) : (
        <div
          className={classNames(
            'relative mx-2 h-1 flex-1 overflow-hidden rounded-sm bg-primary-900 dark:bg-primary-50',
            isLoadingMediaSource || isLoadingBlob ? 'animate-pulse' : '',
          )}
          ref={loaderRef}
        >
          <div
            className={classNames(
              'absolute h-full w-1/5 bg-primary-500 opacity-0',
              isLoadingMediaSource || isLoadingBlob
                ? 'animate-loading-progress-bar opacity-100'
                : '',
            )}
          />
        </div>
      )}
      <span className="w-[40px] text-sm">
        {secondsToTime(Math.floor(leftTime))}
      </span>
    </div>
  )
}
